import React, { useMemo } from 'react';
import type { LanguageType } from '../types/common';
import { getLocale, useIntl } from 'umi';
import {
  transCountryText,
  transLanguage,
  transRegionText,
  uniqueArray,
} from '.';
import { channelInfo } from '@op/components/ChannelLabel';
import useLanguage from '@op/hooks/useLanguage';

export const feedsContentMap = {
  origin: 'content',
  en: 'content_to_en',
  zh: 'content_to_zh',
};

export const feedsLiveContentMap = {
  origin: 'live_title',
  en: 'live_title_en',
  zh: 'live_title_zh',
};

export const videoTagLabelMap = {
  origin: 'video_labels',
  en: 'video_label_ens',
  zh: 'video_label_zhs',
};

export const newsContentMap = {
  origin: 'content',
  en: 'content_en',
  zh: 'content_cn',
};

export const keywordLanguageMap = {
  origin: 'keyword',
  en: 'keyword_en',
  zh: 'keyword_cn',
};

export const feedsTitleMap = {
  origin: 'news_title',
  en: 'title_en',
  zh: 'title_cn',
};

export const feedsTopicMap = {
  en: 'topic_en',
  zh: 'topic_cn',
  origin: 'topic',
};

export const timelineMap = {
  en: 'name_en',
  zh: 'name_cn',
  origin: 'name',
};

export const streamingSummaryMap = {
  en: 'summary_en',
  zh: 'summary_zh',
  origin: 'summary',
};

export function getContentDetail(
  languageMap: { origin: any; en: any; zh: any },
  language: LanguageType,
  item: any,
) {
  const key = languageMap?.[language];
  return item?.[key] || item?.[languageMap['origin']] || '';
}


export function getTextByGlobal(
  languageMap: { origin: any; en: any; zh: any },
  isEn: boolean,
  item: any,
) {
  const language = isEn ? 'en' : 'zh';
  const key = languageMap[language];
  return item[key] || item?.[languageMap?.origin] || '';
}

export function getNameText(item: any, language: LanguageType) {
  return getContentDetail(timelineMap, language, item);
}

export function getFeedsContentText(item: any, language: LanguageType) {
  return getContentDetail(feedsContentMap, language, item);
}

export function getFeedsLiveContentText(item: any, language: LanguageType) {
  return getContentDetail(feedsLiveContentMap, language, item);
}

export function getNewsContentText(item: any, language: LanguageType) {
  return getContentDetail(newsContentMap, language, item);
}

export function getFeedsTitleText(item: any, language: LanguageType) {
  return getContentDetail(feedsTitleMap, language, item);
}

export function getFeedsTopicText(item: any, language: LanguageType) {
  return getContentDetail(feedsTopicMap, language, item);
}

export function getSteamingSummaryText(item: any, language: LanguageType) {
  return getContentDetail(streamingSummaryMap, language, item);
}

export function getFeedsTopicEle(item: any, language: LanguageType) {
  return getContentDetail(feedsTopicMap, language, item)
    .split('|')
    .map((item: string) => <div key={item} className="dbo-tag-item">{item}</div>);
}

export function getVideoLabelEle(item: any, language: LanguageType) {
  let videoTags = getContentDetail(videoTagLabelMap, language, item) || [];
  videoTags = uniqueArray(videoTags);
  return videoTags?.map?.((item: string) => (
    <div className="dbo-tag-item" key={item}>{item}</div>
  ));
}

/**
 * TODO: 其他系统使用
 * 获取keyword中英文映射
 * @param item
 * @param language
 * @returns
 */
export function getFeedsKeywordText(item: any, language: LanguageType) {
  return getContentDetail(keywordLanguageMap, language, item);
}

const languageTypeMap = {
  en: 'en-US',
  zh: 'zh-CN',
};
/**
 * TODO: 其他系统使用
 * 语言选择hook
 * 当页面语言筛选器为源语言时，启用全局多语言选项
 * 语言筛选器为其他时，启用语言筛选器多语言配置
 * @param  languageType    本地语言筛选器值
 * @param  formatMessage   本地翻译
 * @returns
 */
export function useLocalLanguage({
  languageType,
}: {
  languageType: LanguageType;
}) {
  const { formatMessage, isEn } = useLanguage();
  const language = useMemo(() => {
    return languageType === 'origin'
      ? getLocale()?.split('-')?.[0] || 'en'
      : languageType;
  }, [formatMessage, languageType]);

  const languageOther = useMemo(() => {
    return languageType === 'origin'
      ? getLocale() || 'en-US'
      : languageTypeMap[languageType];
  }, [formatMessage, languageType]);

  return { language, formatMessage, languageOther, isEn };
}

export const dealWithOthersName = (
  name: string,
  type: 'language' | 'country' | 'region' | string = '',
  local: string = getLocale()
) => {

  if (['others', 'other'].includes(name?.toLocaleLowerCase())) {
    return local === 'en-US' ? 'Others' : '其他';
  }

  if (name === 'Unidentified') {
    return local === 'en-US' ? 'Unidentified' : '未区分国家/地区';
  }

  if (name === 'Unlabeled reviews') {
    return local === 'en-US' ? 'Unlabeled reviews' : '未标记的评论';
  }

  if (type.includes('language')) {
    return transLanguage(name, local);
  }

  if (type.includes('country')) {
    return transCountryText(name, local);
  }

  if (type.includes('region')) {
    return transRegionText(name, 'Unidentified', local);
  }

  if (type.includes('channel')) {
    return channelInfo(name as any)?.name;
  }

  return name;
};
